import { trpc } from "@/lib/trpc";
import { CloudOff } from "lucide-react";
import { toast } from "sonner";

export function QuickBooksTab() {
	const utils = trpc.useUtils();
	const { data: qboStatus, isLoading } = trpc.qbo.status.useQuery();
	const syncHistory = trpc.qbo.syncInvoiceHistory.useMutation();
	const disconnect = trpc.qbo.disconnect.useMutation({
		onSuccess: () => {
			// Invalidate the status query so the UI flips to "Not connected"
			// without needing window.location.reload(). Other QBO-derived
			// queries on this page (Connection Details, Invoice History) are
			// gated by qboStatus.connected and unmount automatically.
			utils.qbo.status.invalidate();
			toast.success("QuickBooks disconnected");
		},
		onError: (err) => toast.error(err.message),
	});

	if (isLoading) {
		return (
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<div className="h-10 w-48 animate-pulse rounded bg-[#f3f4f6]" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
				<h2 className="mb-4 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
					Connection
				</h2>

				{!qboStatus?.configured ? (
					<div className="flex flex-col items-center rounded-lg border border-dashed border-[#e5e7eb] bg-[#f8f9fb] py-8">
						<CloudOff className="h-8 w-8 text-[#e5e7eb]" strokeWidth={1} />
						<p className="mt-3 text-sm font-medium text-[#1e1e20]">QuickBooks not configured</p>
						<p className="mt-1 max-w-sm text-center text-xs text-[#9ca3af]">
							Set QBO_CLIENT_ID and QBO_CLIENT_SECRET environment variables to enable the QuickBooks
							integration.
						</p>
					</div>
				) : !qboStatus.connected ? (
					<div className="flex items-center gap-4">
						<span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#e5e7eb]" />
						<span className="text-sm text-[#6b7280]">Not connected</span>
						<a
							href="/auth/qbo/connect"
							className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold leading-9 text-white transition-colors duration-150 hover:bg-[#15803d]"
						>
							Connect to QuickBooks
						</a>
					</div>
				) : (
					<div className="flex items-center gap-4">
						<span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#15803d]" />
						<span className="text-sm font-medium text-[#1e1e20]">
							Connected to {qboStatus.companyName ?? qboStatus.realmId}
						</span>
						<button
							type="button"
							onClick={() => disconnect.mutate({})}
							disabled={disconnect.isPending}
							className="h-9 rounded border border-[#e5e7eb] bg-white px-3 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-50"
						>
							{disconnect.isPending ? "Disconnecting..." : "Disconnect"}
						</button>
					</div>
				)}
			</div>

			{qboStatus?.connected && (
				<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
					<h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Connection Details
					</h2>
					<div className="grid gap-4 sm:grid-cols-2">
						{qboStatus.companyName && (
							<div>
								<p className="text-xs text-[#9ca3af]">Company</p>
								<p className="text-sm font-medium text-[#1e1e20]">{qboStatus.companyName}</p>
							</div>
						)}
						<div>
							<p className="text-xs text-[#9ca3af]">Realm ID</p>
							<p className="text-sm font-mono text-[#1e1e20]">{qboStatus.realmId}</p>
						</div>
						<div>
							{/* This is the OAuth access token (1-hour TTL), which
							    refreshTokenIfNeeded renews on demand — NOT the life of the
							    QuickBooks connection. The old "Token Expires" label read as
							    the connection dying today and sent the client hunting for a
							    problem that wasn't there. */}
							<p className="text-xs text-[#9ca3af]">Access Token Renews</p>
							<p className="text-sm text-[#1e1e20]">
								{qboStatus.tokenExpiresAt
									? new Date(qboStatus.tokenExpiresAt).toLocaleString()
									: "--"}
							</p>
							<p className="text-xs text-[#9ca3af]">Renews automatically — no action needed.</p>
						</div>
					</div>
				</div>
			)}

			{qboStatus?.connected && (
				<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
					<h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						Invoice History
					</h2>
					<p className="mb-4 text-xs text-[#9ca3af]">
						Sync previous invoices from QuickBooks for all payroll customers.
					</p>
					<button
						type="button"
						onClick={async () => {
							try {
								const result = await syncHistory.mutateAsync();
								toast.success(
									`Synced ${result.synced} invoices across ${result.customers} customers`,
								);
							} catch (error) {
								toast.error(
									error instanceof Error ? error.message : "Failed to sync invoice history",
								);
							}
						}}
						disabled={syncHistory.isPending}
						className="rounded bg-[#4f46e5] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#4338ca] disabled:opacity-50"
					>
						{syncHistory.isPending ? "Syncing..." : "Sync Invoice History"}
					</button>
				</div>
			)}
		</div>
	);
}
